import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { createProjectAdmin, updateProjectAdmin, getAllProjectsAdmin } from '../../services/adminApi';
import { useToast } from '../../hooks/use-toast';
import { ArrowLeft, Plus, X } from 'lucide-react';

const AdminProjectForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    category: 'residential',
    subcategory: 'interiors',
    description: '',
    location: '',
    year: new Date().getFullYear().toString(),
    images: ['']
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProject, setLoadingProject] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode) {
      loadProject();
    }
  }, [id]);

  const loadProject = async () => {
    try {
      const projects = await getAllProjectsAdmin();
      const project = projects.find(p => p.id === id);
      if (project) {
        setFormData({
          title: project.title,
          category: project.category,
          subcategory: project.subcategory,
          description: project.description,
          location: project.location,
          year: project.year,
          images: project.images
        });
      } else {
        toast({
          title: 'Error',
          description: 'Project not found',
          variant: 'destructive'
        });
        navigate('/admin/dashboard');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load project',
        variant: 'destructive'
      });
      navigate('/admin/dashboard');
    } finally {
      setLoadingProject(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
  };

  const addImageField = () => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, '']
    }));
  };

  const removeImageField = (index) => {
    if (formData.images.length > 1) {
      const newImages = formData.images.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        images: newImages
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Filter out empty image URLs
    const projectData = {
      ...formData,
      images: formData.images.filter(img => img.trim() !== '')
    };

    if (projectData.images.length === 0) {
      toast({
        title: 'Error',
        description: 'Please add at least one image URL',
        variant: 'destructive'
      });
      setIsLoading(false);
      return;
    }

    try {
      if (isEditMode) {
        await updateProjectAdmin(id, projectData);
        toast({
          title: 'Success',
          description: 'Project updated successfully'
        });
      } else {
        await createProjectAdmin(projectData);
        toast({
          title: 'Success',
          description: 'Project created successfully'
        });
      }
      navigate('/admin/dashboard');
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${isEditMode ? 'update' : 'create'} project`,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingProject) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center">
        <div className="text-[#4b63eb] text-lg">Loading project...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f10] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center space-x-2 text-[#4b63eb] hover:text-[#3b4ec9] mb-4"
          >
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </Link>
          <h1 className="text-3xl font-bold text-[#f5f5f5]">
            {isEditMode ? 'Edit Project' : 'Add New Project'}
          </h1>
          <p className="text-[#a0a0a0] mt-2">
            {isEditMode ? 'Update project details and images' : 'Create a new portfolio project'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-[#1a1a1a] p-8 rounded-lg border border-[#2d3748] space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-semibold text-[#f5f5f5] mb-2">
              Project Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-[#0f0f10] border border-[#2d3748] text-[#f5f5f5] rounded focus:border-[#4b63eb] focus:outline-none focus:ring-1 focus:ring-[#4b63eb]"
              placeholder="e.g., Modern Downtown Residence"
            />
          </div>

          {/* Category & Subcategory */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="block text-sm font-semibold text-[#f5f5f5] mb-2">
                Category *
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#0f0f10] border border-[#2d3748] text-[#f5f5f5] rounded focus:border-[#4b63eb] focus:outline-none focus:ring-1 focus:ring-[#4b63eb]"
              >
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>
            <div>
              <label htmlFor="subcategory" className="block text-sm font-semibold text-[#f5f5f5] mb-2">
                Subcategory *
              </label>
              <select
                id="subcategory"
                name="subcategory"
                value={formData.subcategory}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#0f0f10] border border-[#2d3748] text-[#f5f5f5] rounded focus:border-[#4b63eb] focus:outline-none focus:ring-1 focus:ring-[#4b63eb]"
              >
                <option value="interiors">Interiors</option>
                <option value="exteriors">Exteriors</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-semibold text-[#f5f5f5] mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-4 py-3 bg-[#0f0f10] border border-[#2d3748] text-[#f5f5f5] rounded focus:border-[#4b63eb] focus:outline-none focus:ring-1 focus:ring-[#4b63eb] resize-none"
              placeholder="Brief description of the project..."
            />
          </div>

          {/* Location & Year */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="location" className="block text-sm font-semibold text-[#f5f5f5] mb-2">
                Location *
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#0f0f10] border border-[#2d3748] text-[#f5f5f5] rounded focus:border-[#4b63eb] focus:outline-none focus:ring-1 focus:ring-[#4b63eb]"
                placeholder="e.g., Jacksonville, FL"
              />
            </div>
            <div>
              <label htmlFor="year" className="block text-sm font-semibold text-[#f5f5f5] mb-2">
                Year *
              </label>
              <input
                type="text"
                id="year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#0f0f10] border border-[#2d3748] text-[#f5f5f5] rounded focus:border-[#4b63eb] focus:outline-none focus:ring-1 focus:ring-[#4b63eb]"
                placeholder="e.g., 2024"
              />
            </div>
          </div>

          {/* Images */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-semibold text-[#f5f5f5]">
                Image URLs * (Google Photos, Imgur, etc.)
              </label>
              <button
                type="button"
                onClick={addImageField}
                className="flex items-center space-x-1 text-[#4b63eb] hover:text-[#3b4ec9] text-sm"
              >
                <Plus size={16} />
                <span>Add Image</span>
              </button>
            </div>
            <div className="space-y-3">
              {formData.images.map((image, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="url"
                    value={image}
                    onChange={(e) => handleImageChange(index, e.target.value)}
                    className="flex-1 px-4 py-3 bg-[#0f0f10] border border-[#2d3748] text-[#f5f5f5] rounded focus:border-[#4b63eb] focus:outline-none focus:ring-1 focus:ring-[#4b63eb]"
                    placeholder="https://example.com/image.jpg"
                  />
                  {formData.images.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeImageField(index)}
                      className="p-3 bg-[#2d3748] hover:bg-[#f04848] text-[#f5f5f5] rounded transition-colors"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm text-[#a0a0a0] mt-2">
              Tip: Use direct image URLs from Google Photos (right-click → Copy image address)
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-[#4b63eb] to-[#3b4ec9] text-white py-3 rounded font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-[#4b63eb]/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : (isEditMode ? 'Update Project' : 'Create Project')}
            </button>
            <Link
              to="/admin/dashboard"
              className="px-8 py-3 bg-[#2d3748] hover:bg-[#3d4758] text-[#f5f5f5] rounded font-semibold transition-colors text-center"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProjectForm;
